import { atom, useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';

import { getDistance, getMapBounds, useGoogleMapInstance } from '~utils/map';

import { DoctorResult, SanityDoctor, SanityDoctorOffice, SanityDoctorSearchBadge, SanityDoctorSearchSort } from '~types/sanity';

import { useUserLocation } from './use-user-location';
import { DISTANCE_FILTER_QUERY } from '~components/DistanceFilter';
import { useStaticQuery } from 'gatsby';

export const resultFiltersAtom = atom<{
  distance: number | null;
  consultation: string | null;
  gender: string | null;
  language: string | null;
  badge: SanityDoctorSearchBadge;
  sort: SanityDoctorSearchSort;
}>({
  distance: null,
  consultation: null,
  gender: null,
  language: null,
  badge: null,
  sort: 'Experience'
});

export const mobileResultsViewAtom = atom<'list' | 'map'>('list');

export interface SelectedDoctorProps {
  doctor: DoctorResult['id'];
  office: DoctorResult['_key'];
}

const getIsAlreadyInArray = (
  array: DoctorResult[],
  o: Omit<DoctorResult, 'distance'>,
) => {
  return array.some((office) => office.id === o.id);
};

const getMainOffice = (
  array: Omit<DoctorResult, 'distance'>[],
  o: Omit<DoctorResult, 'distance'>,
) => {
  return array.filter((office) => office.id === o.id && office.mainOffice)[0] as DoctorResult;
};

export const useDoctorResults = (
  doctors: SanityDoctor[],
  pageContext: { boundary?: string; abbreviation?: string },
) => {
  const userLocation = useUserLocation();

  const { boundary, abbreviation } = pageContext;

  const { distance, badge, sort } = useAtomValue(resultFiltersAtom);
  const { allSanityDistanceFilter } = useStaticQuery(DISTANCE_FILTER_QUERY);

  const distanceRadius =
    allSanityDistanceFilter.nodes.find(
      (n: { defaultValue: boolean; value: number }) => n.defaultValue,
    )?.value ?? 100;

  // const distanceRadius = distance ? distance : 100000;

  const [doctorResults, setDoctorResults] = useState<DoctorResult[] | null>(
    null,
  );
  const [nullStateDoctors, setNullStateDoctors] = useState<
    DoctorResult[] | null
  >(null);

  const [addressDisplay, setAddressDisplay] = useState<string>('');

  const [selectedDoctor, setSelectedDoctor] =
    useState<SelectedDoctorProps | null>(null);

  const allOffices = useMemo(() => {
    return doctors.reduce(
      (array, doctor) => {
        doctor.doctorOffices.map((o) => {
          array.push({
            ...o,
            name: doctor.name,
            id: doctor.id,
            credentials: doctor.credentials,
            consultations: doctor.consultations,
            badges: doctor.badges,
            image: doctor.gallery[0],
            slug: doctor.slug,
            link: `/find-a-doctor/doctors/${doctor.slug.current}?office=${o._key}`,
            contactLink: `/find-a-doctor/doctors/${doctor.slug.current}?office=${o._key}&contact=true`,
            listCard: false,
            additionalOffices: doctor.doctorOffices.filter(
              (office) => office._key !== o._key,
            ),
            numCases: 0,
            voomDoctorId: doctor.doctorId,
            searchBadges: doctor.searchBadges,
            telehealthEmbedCode: doctor.telehealthEmbedCode
          });
        });
        return array;
      },
      [] as Omit<DoctorResult, 'distance'>[],
    );
  }, [doctors]);

  const { isLoaded, googleMapInstance, onLoad, onUnmount } =
    useGoogleMapInstance();

  const count =
    doctorResults?.filter((doctor) => doctor.listCard === true).length ?? 0;

  const badgeSortNumber = (badges: SanityDoctorSearchBadge[]) => badges.includes('Gold Badge') ? 3 : badges.includes('Silver Badge') ? 2 : badges.includes('Bronze Badge') ? 1 : 0;

  useEffect(() => {
    const getAddressToDisplay = async () => {
      if (userLocation !== null && !userLocation.address && googleMapInstance) {
        const geocoder = new google.maps.Geocoder();
        const latLng = new google.maps.LatLng(
          userLocation?.lat ?? 0,
          userLocation?.lng ?? 0,
        );

        const { results } = await geocoder.geocode({
          location: latLng,
        });
        const city = results[0].address_components.find((c) =>
          c.types.includes('locality'),
        )?.long_name;
        const state = results[0].address_components.find((c) =>
          c.types.includes('administrative_area_level_1'),
        )?.short_name;
      }
    };
    getAddressToDisplay();
  }, [googleMapInstance, userLocation, count, boundary]);

  useEffect(() => {
    let foundOffices: DoctorResult[] = [];
    setNullStateDoctors([]);
    if (googleMapInstance && boundary) {
      // get boundary location and find offices inside that boundary
      const officesInBoundary = allOffices.reduce((array, o, index, fullArray) => {
        const isAlreadyInArray = getIsAlreadyInArray(array, o);
        if (
          !isAlreadyInArray && (o.state.name === boundary ||
            `${o.city.name}, ${o.state.abbreviation}` === boundary)
        ) {
          const officeToAdd = getMainOffice(fullArray, o) ?? o;
          array.push({
            ...officeToAdd,
            distance: null,
            listCard: isAlreadyInArray ? false : true,
          });
        }
        return array;
      }, [] as DoctorResult[]);

      foundOffices = officesInBoundary;
      setDoctorResults(officesInBoundary);

      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ address: boundary }, function (results, status) {
        if (status == google.maps.GeocoderStatus.OK && results) {
          // get viewport of boundary. need abbreviation to get viewport of state due to Washington DC
          const bounds =
            results.find((r) => {
              return r.address_components.find(
                (c) => c.short_name === abbreviation,
              );
            })?.geometry.viewport ?? results[0].geometry.viewport;

          // extend bounds to make sure all offices are in viewport
          officesInBoundary.map(({ geolocation }) => {
            bounds.extend(
              new window.google.maps.LatLng(geolocation.lat, geolocation.lng),
            );
          });
          googleMapInstance.setCenter(results[0].geometry.location);

          googleMapInstance.fitBounds(bounds);
        }
      });
    } else if (googleMapInstance) {
      let officesInArea = allOffices
        .reduce((array, o) => {
          if (!userLocation) {
            array.push({ ...o, distance: 0 });
            return array;
          }
          const distance = getDistance(userLocation, o.geolocation);
          if (distance < distanceRadius && (badge === null || o.searchBadges.some(b => b === badge))) {
            array.push({ ...o, distance });
          }
          return array;
        }, [] as DoctorResult[])
        .sort((a, b) => sort == 'Experience' ? badgeSortNumber(b.searchBadges) - badgeSortNumber(a.searchBadges) || (a?.distance ?? 0) - (b?.distance ?? 0) : (a?.distance ?? 0) - (b?.distance ?? 0) || badgeSortNumber(b.searchBadges) - badgeSortNumber(a.searchBadges)) // sort by badges, then distance, then remove duplicates
        .reduce((array, o, index, fullArray) => {
          const isAlreadyInArray = getIsAlreadyInArray(array, o);
          if (!isAlreadyInArray) {
            const officeToAdd = getMainOffice(fullArray, o) ?? o;
            array.push({ ...officeToAdd, listCard: isAlreadyInArray ? false : true });
          }
          return array;
        }, [] as DoctorResult[]);

      // If the search location is NYC, only show badged offices in NYC, nonbadged can show all
      if (userLocation?.address?.startsWith('New York, NY')) {
        officesInArea = officesInArea.filter(o => !o.searchBadges.length || (o.city.name === 'New York' && o.state.abbreviation === 'NY'));
      }

      const drBlitz = officesInArea.filter(o => o.voomDoctorId === 'Neal Blitz, DPM');
      const otherDrs = officesInArea.filter(o => o.voomDoctorId !== 'Neal Blitz, DPM');
      foundOffices = [...drBlitz, ...otherDrs];
      setDoctorResults(foundOffices);
      // } else {
      //   foundOffices = officesInArea;
      //   setDoctorResults(officesInArea);
      // }
    }

    const hasSearchBadgeOffices = foundOffices.some(o => o.searchBadges.length);
    if (googleMapInstance && userLocation && ((foundOffices?.length ?? 0) < 3 || !hasSearchBadgeOffices)) {
      const offices = foundOffices?.length ? allOffices.filter(o => foundOffices?.every(r => r.id !== o.id)) : allOffices;
      const closestBadgedOffice = offices.filter(o => o.searchBadges.some(b => b))
        .reduce((array, o) => {
          const distance = getDistance(userLocation, o.geolocation);
          array.push({ ...o, distance, listCard: true });
          return array.sort((a, b) => (a?.distance ?? 0) - (b?.distance ?? 0));
        }, [] as DoctorResult[])[0];

      let addNum = 3 - foundOffices.length;
      if (!hasSearchBadgeOffices) {
        foundOffices.unshift(closestBadgedOffice);
        addNum--;
      }

      const closestThreeOffices = offices.filter(o => o.id !== closestBadgedOffice.id && (badge === null || o.searchBadges.some(b => b === badge)))
        .reduce((array, o, index, fullArray) => {
          const isAlreadyInArray = getIsAlreadyInArray(array, o);
          const distance = getDistance(userLocation, o.geolocation);
          if (!isAlreadyInArray) {
            const officeToAdd = getMainOffice(fullArray, o) ?? o;
            array.push({ ...officeToAdd, distance, listCard: true });
          }
          return array.sort((a, b) => (a?.distance ?? 0) - (b?.distance ?? 0));
        }, [] as DoctorResult[])
        .splice(0, addNum);
      foundOffices = [...foundOffices, ...closestThreeOffices];
      if (sort === 'Distance') foundOffices = foundOffices.sort((a, b) => (a?.distance ?? 0) - (b?.distance ?? 0));
      setDoctorResults(foundOffices);
    }

    const geoLocations = foundOffices.map((o) => {
      return o.geolocation;
    });
    if (geoLocations.length > 0) {
      if (userLocation) {
        geoLocations.push(userLocation); // add userLocation
      }
      googleMapInstance && getMapBounds(googleMapInstance, geoLocations);
    }
  }, [
    doctors,
    googleMapInstance,
    userLocation,
    allOffices,
    distanceRadius,
    badge,
    boundary,
    abbreviation,
    sort
  ]);

  return {
    isLoaded,
    onLoad,
    onUnmount,
    googleMapInstance,
    addressDisplay,
    selectedDoctor,
    setSelectedDoctor,
    userLocation,
    doctorResults,
    nullStateDoctors,
  };
};
